/**
 * /queries: the 2D query log. Lists every query with filters, sort, row detail, response and
 * status actions, CSV export and a summary strip. Live updates arrive over the WebSocket.
 */
import '@fontsource-variable/sora';
import '@fontsource-variable/inter';
import {
  DASHBOARD_TITLES,
  QUERY_CATEGORIES,
  QUERY_PRIORITIES,
  QUERY_STATUSES,
  ROLES,
  ROLE_STYLES,
  canModifyQueries,
  queryAgeDays,
  type Manifest,
  type Query,
  type QueryListResponse,
  type QuerySummary,
  type Role,
  type RoomSummary,
} from '@clinspace/shared';
import { ApiError, api, getJson, page, sendJson } from '../lib/api.ts';
import { loadItem, saveItem } from '../lib/storage.ts';
import { SocketClient } from '../lib/ws.ts';
import { WARD, layoutWard, wardRoom } from '../ward/layout.ts';
import {
  ARC,
  CENTRE,
  HALL,
  PRESENTATION,
  STUDIO,
  STUDIO_FITTINGS,
  TABLE,
  aroundArc,
  arcPlan,
  hallOutline,
  type Point,
} from '../world/plan.ts';

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

/* ---------- Identity ---------- */

const meName = $<HTMLInputElement>('me-name');
const meRole = $<HTMLSelectElement>('me-role');
for (const role of ROLES) meRole.append(new Option(role, role));
meName.value = loadItem<string>('name', '');
meRole.value = loadItem<Role>('role', 'Clinical Data Manager');
meName.addEventListener('change', () => saveItem('name', meName.value.trim()));
meRole.addEventListener('change', () => {
  saveItem('role', meRole.value);
  renderRows();
});
const identity = () => ({ name: meName.value.trim() || 'Anonymous', role: meRole.value as Role });

/* ---------- Columns ---------- */

interface Column {
  key: string;
  label: string;
  render: (q: Query) => string | HTMLElement;
  sort?: string;
}

const esc = (s: unknown) =>
  String(s ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
const pill = (value: string) => `<span class="pill ${esc(value)}">${esc(value)}</span>`;
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const columns: Column[] = [
  { key: 'raisedAt', label: 'Raised', render: (q) => fmtDate(q.raisedAt), sort: 'raisedAt' },
  { key: 'study', label: 'Study', render: (q) => esc(q.study), sort: 'study' },
  { key: 'subjectId', label: 'Subject', render: (q) => esc(q.subjectId), sort: 'subjectId' },
  { key: 'siteId', label: 'Site', render: (q) => esc(q.siteId), sort: 'siteId' },
  { key: 'visit', label: 'Visit', render: (q) => esc(q.visit ?? ''), sort: 'visit' },
  { key: 'form', label: 'Form', render: (q) => esc(q.form), sort: 'form' },
  { key: 'field', label: 'Field', render: (q) => esc(q.field ?? ''), sort: 'field' },
  {
    key: 'currentValue',
    label: 'Value',
    render: (q) => `<span title="${esc(q.currentValue)}">${esc(q.currentValue ?? '')}</span>`,
  },
  { key: 'category', label: 'Category', render: (q) => esc(q.category), sort: 'category' },
  { key: 'priority', label: 'Priority', render: (q) => pill(q.priority), sort: 'priority' },
  { key: 'status', label: 'Status', render: (q) => pill(q.status), sort: 'status' },
  {
    key: 'raisedBy',
    label: 'Raised by',
    render: (q) =>
      `<span class="role-chip" style="background:${ROLE_STYLES[q.raisedBy.role].hex}"></span>${esc(q.raisedBy.name)} <small>(${esc(ROLE_STYLES[q.raisedBy.role].short)})</small>`,
    sort: 'raisedByName',
  },
  { key: 'ageDays', label: 'Age (d)', render: (q) => String(queryAgeDays(q)), sort: 'ageDays' },
  {
    key: 'responseCount',
    label: 'Resp.',
    render: (q) => String(q.responses.length),
    sort: 'responseCount',
  },
];

/* ---------- State ---------- */

interface Filters {
  q: string;
  study: string;
  status: string[];
  siteId: string;
  category: string;
  priority: string;
  raisedBy: string;
  from: string;
  to: string;
  sort: string;
  dir: 'asc' | 'desc';
}

const filters: Filters = {
  q: '',
  study: '',
  status: [],
  siteId: '',
  category: '',
  priority: '',
  raisedBy: '',
  from: '',
  to: '',
  sort: 'raisedAt',
  dir: 'desc',
  ...loadItem<Partial<Filters>>('queries.filters', {}),
};

let items: Query[] = [];
let total = 0;
let expandedId: string | undefined;
let manifest: Manifest | undefined;
let rooms: RoomSummary[] = [];

/* ---------- Filter controls ---------- */

const fq = $<HTMLInputElement>('f-q');
const fStudy = $<HTMLSelectElement>('f-study');
const fStatus = $<HTMLSelectElement>('f-status');
const fSite = $<HTMLSelectElement>('f-site');
const fCategory = $<HTMLSelectElement>('f-category');
const fPriority = $<HTMLSelectElement>('f-priority');
const fRaisedBy = $<HTMLInputElement>('f-raisedBy');
const fFrom = $<HTMLInputElement>('f-from');
const fTo = $<HTMLInputElement>('f-to');

for (const s of QUERY_STATUSES) fStatus.append(new Option(s, s, false, filters.status.includes(s)));
for (const c of QUERY_CATEGORIES) fCategory.append(new Option(c, c));
for (const p of QUERY_PRIORITIES) fPriority.append(new Option(p, p));
fq.value = filters.q;
fCategory.value = filters.category;
fPriority.value = filters.priority;
fRaisedBy.value = filters.raisedBy;
fFrom.value = filters.from;
fTo.value = filters.to;

function readFilters(): void {
  filters.q = fq.value.trim();
  filters.study = fStudy.value;
  filters.status = [...fStatus.selectedOptions].map((o) => o.value);
  filters.siteId = fSite.value;
  filters.category = fCategory.value;
  filters.priority = fPriority.value;
  filters.raisedBy = fRaisedBy.value.trim();
  filters.from = fFrom.value;
  filters.to = fTo.value;
  saveItem('queries.filters', filters);
}

let debounce: number | undefined;
const onFilterChange = () => {
  readFilters();
  clearTimeout(debounce);
  debounce = window.setTimeout(() => void load(), 150);
};
for (const el of [fq, fStudy, fStatus, fSite, fCategory, fPriority, fRaisedBy, fFrom, fTo])
  el.addEventListener('input', onFilterChange);
// Clear keeps the study: the log only ever shows one study's queries.
$<HTMLButtonElement>('f-clear').addEventListener('click', () => {
  fq.value =
    fSite.value =
    fCategory.value =
    fPriority.value =
    fRaisedBy.value =
    fFrom.value =
    fTo.value =
      '';
  for (const o of fStatus.options) o.selected = false;
  onFilterChange();
});

function filterParams(): Record<string, string | number | undefined> {
  return {
    q: filters.q,
    study: filters.study,
    status: filters.status.join(','),
    siteId: filters.siteId,
    category: filters.category,
    priority: filters.priority,
    raisedBy: filters.raisedBy,
    from: filters.from,
    to: filters.to,
    sort: filters.sort,
    dir: filters.dir,
    limit: 500,
  };
}

function updateExportLink(): void {
  const link = $<HTMLAnchorElement>('export');
  link.classList.toggle('disabled', !filters.study);
  link.setAttribute('aria-disabled', String(!filters.study));
  if (!filters.study) {
    link.href = '#';
    return;
  }
  const url = new URL(api('queries.csv'), location.origin);
  for (const [k, v] of Object.entries(filterParams()))
    if (v !== undefined && v !== '' && k !== 'limit') url.searchParams.set(k, String(v));
  $<HTMLAnchorElement>('export').href = url.toString();
}

/* ---------- Loading ---------- */

async function loadMeta(): Promise<void> {
  manifest = await getJson<Manifest>(api('studies'));
  for (const s of manifest.studies) {
    fStudy.append(new Option(`${s.code} · ${s.shortTitle}`, s.code));
    sStudy.append(new Option(`${s.code} · ${s.shortTitle}`, s.code));
  }
  // No study until one is chosen (a remembered one that no longer exists is dropped).
  if (!manifest.studies.some((s) => s.code === filters.study)) filters.study = '';
  fStudy.value = filters.study;
  await refreshSites();
}

async function refreshSites(): Promise<void> {
  const codes = filters.study ? [filters.study] : [];
  const seen = new Set<string>();
  fSite.innerHTML = '<option value="">All sites</option>';
  for (const code of codes) {
    const study = await getJson<{ sites: { id: string; name: string; city: string }[] }>(
      api('studies', code),
    );
    for (const site of study.sites) {
      if (seen.has(site.id)) continue;
      seen.add(site.id);
      fSite.append(new Option(`${site.id} · ${site.city}`, site.id));
    }
  }
  fSite.value = seen.has(filters.siteId) ? filters.siteId : '';
}

/* ---------- Room snapshots tab (2026-10-06: back for everyone, view only) ---------- */

interface Snapshot {
  file: string;
  kind: 'whiteboard' | 'screenshot';
  by: string;
  at: string;
  bytes: number;
  url: string;
}

const sStudy = $<HTMLSelectElement>('s-study');
const sKind = $<HTMLSelectElement>('s-kind');

async function loadSnapshots(): Promise<void> {
  const grid = $<HTMLDivElement>('s-grid');
  const count = $<HTMLParagraphElement>('s-count');
  sStudy.value = filters.study;
  if (!filters.study) {
    count.textContent = '';
    grid.innerHTML = '<p class="empty">Choose a study above to see its room snapshots.</p>';
    return;
  }
  const res = await getJson<{ items: Snapshot[] }>(api('rooms', filters.study, 'snapshots'));
  const items = res.items.filter((s) => !sKind.value || s.kind === sKind.value);
  count.textContent = items.length
    ? `${items.length} snapshot${items.length === 1 ? '' : 's'} from ${filters.study}'s rooms, newest first. Select one to open it full size.`
    : '';
  grid.innerHTML = items.length
    ? items
        .map(
          (s) => `<figure class="snap">
            <a class="snap-image" href="${esc(s.url)}" target="_blank" rel="noopener" title="Open full size">
              <img src="${esc(s.url)}" alt="${esc(s.kind)} from ${esc(filters.study)}" loading="lazy" />
            </a>
            <figcaption>
              <span class="snap-title">${esc(s.kind === 'whiteboard' ? 'Whiteboard' : 'Screenshot')}</span>
              <span class="snap-meta">${esc(s.by.replace(/-/g, ' '))} · ${esc(fmtDate(s.at))}</span>
            </figcaption>
          </figure>`,
        )
        .join('')
    : `<p class="empty">No snapshots in ${esc(filters.study)} yet. In a room, press "Snapshot to PNG" on the whiteboard tray, or frame a view with both hands (Z on desktop).</p>`;
}

sStudy.addEventListener('change', () => {
  fStudy.value = sStudy.value;
  readFilters();
  void load();
  void loadSnapshots();
});
sKind.addEventListener('change', () => void loadSnapshots());

/** Query log or room snapshots: one page, two tabs (#snapshots opens the second). */
function showTab(tab: 'queries' | 'snapshots'): void {
  for (const b of document.querySelectorAll<HTMLButtonElement>('.page-tab'))
    b.classList.toggle('current', b.dataset.tab === tab);
  $<HTMLDivElement>('tab-queries').hidden = tab !== 'queries';
  $<HTMLElement>('tab-snapshots').hidden = tab !== 'snapshots';
  history.replaceState(null, '', tab === 'snapshots' ? '#snapshots' : location.pathname);
  const title = tab === 'snapshots' ? 'Room snapshots' : 'Query log';
  (document.querySelector('.topbar h1') as HTMLHeadingElement).textContent = title;
  document.title = `ClinSpaceXR ${title}`;
  if (tab === 'snapshots') void loadSnapshots();
}
for (const b of document.querySelectorAll<HTMLButtonElement>('.page-tab'))
  b.addEventListener('click', () => showTab(b.dataset.tab as 'queries' | 'snapshots'));

let lastStudyFilter = filters.study;

async function load(): Promise<void> {
  if (lastStudyFilter !== filters.study) {
    lastStudyFilter = filters.study;
    await refreshSites();
  }
  updateExportLink();
  // Nothing is shown until a study is chosen, and then only that study (2026-10-06).
  $<HTMLElement>('summary').hidden = !filters.study;
  if (!filters.study) {
    items = [];
    total = 0;
    renderHead();
    $<HTMLParagraphElement>('status-line').textContent = '';
    body.innerHTML = `<tr><td colspan="${columns.length}" class="empty">Choose a study above to see its queries.</td></tr>`;
    return;
  }
  const res = await getJson<QueryListResponse>(api('queries'), filterParams());
  items = res.items;
  total = res.total;
  renderRows();
  void loadSummary();
}

async function loadSummary(): Promise<void> {
  if (!filters.study) return;
  const summary = await getJson<QuerySummary>(api('queries', 'summary'), {
    study: filters.study,
  });
  const cards = $<HTMLDivElement>('summary-cards');
  cards.innerHTML = summary.perStudy
    .map(
      (s) => `<div class="card"><div class="study">${esc(s.study)}</div><div class="counts">
        <span class="open"><b>${s.open}</b>open</span><span class="answered"><b>${s.answered}</b>answered</span><span class="closed"><b>${s.closed}</b>closed</span></div></div>`,
    )
    .join('');
  const svg = $<SVGSVGElement & HTMLElement>('trend');
  const points = summary.trend30d;
  const max = Math.max(1, ...points.map((p) => p.open));
  const path = points
    .map(
      (p, i) =>
        `${i === 0 ? 'M' : 'L'}${(i / (points.length - 1)) * 300},${58 - (p.open / max) * 52}`,
    )
    .join(' ');
  const area = `${path} L300,60 L0,60 Z`;
  svg.innerHTML = `<path d="${area}" fill="rgba(21,147,184,0.18)"/><path d="${path}" fill="none" stroke="#1f8a8a" stroke-width="2"/>
    <text x="2" y="12" font-size="10" fill="currentColor" opacity="0.7">max ${max}</text>
    <text x="298" y="12" font-size="10" fill="currentColor" opacity="0.7" text-anchor="end">now ${points.at(-1)?.open ?? 0}</text>`;
}

/* ---------- Rendering ---------- */

const headRow = $<HTMLTableRowElement>('head-row');
headRow.innerHTML = columns
  .map(
    (c) =>
      `<th data-key="${c.key}" data-sort="${c.sort ?? ''}">${esc(c.label)}<span class="arrow"></span></th>`,
  )
  .join('');
headRow.addEventListener('click', (e) => {
  const th = (e.target as HTMLElement).closest('th');
  const sort = th?.dataset.sort;
  if (!sort) return;
  if (filters.sort === sort) filters.dir = filters.dir === 'asc' ? 'desc' : 'asc';
  else {
    filters.sort = sort;
    filters.dir =
      sort === 'raisedAt' || sort === 'ageDays' || sort === 'responseCount' ? 'desc' : 'asc';
  }
  saveItem('queries.filters', filters);
  void load();
});

function renderHead(): void {
  for (const th of headRow.querySelectorAll('th')) {
    const active = th.dataset.sort === filters.sort;
    th.classList.toggle('sorted', active);
    th.querySelector('.arrow')!.textContent = active ? (filters.dir === 'asc' ? '▲' : '▼') : '';
  }
}

const body = $<HTMLTableSectionElement>('body');

function renderRows(): void {
  renderHead();
  $<HTMLParagraphElement>('status-line').textContent =
    `${items.length} of ${total} queries${total > items.length ? ' (showing the first 500; narrow the filters to see the rest)' : ''}`;
  if (items.length === 0) {
    body.innerHTML = `<tr><td colspan="${columns.length}" class="empty">No queries match these filters.</td></tr>`;
    return;
  }
  const frag = document.createDocumentFragment();
  for (const q of items) {
    const tr = document.createElement('tr');
    tr.className = `row${q.id === expandedId ? ' open' : ''}`;
    tr.dataset.id = q.id;
    tr.innerHTML = columns
      .map((c) => {
        const v = c.render(q);
        return `<td class="${c.key === 'currentValue' ? 'value' : ''}">${typeof v === 'string' ? v : v.outerHTML}</td>`;
      })
      .join('');
    tr.addEventListener('click', () => toggle(q.id));
    frag.append(tr);
    if (q.id === expandedId) frag.append(detailRow(q));
  }
  body.replaceChildren(frag);
}

function toggle(id: string): void {
  expandedId = expandedId === id ? undefined : id;
  renderRows();
}

const detailTemplate = $<HTMLTemplateElement>('row-detail');

function detailRow(q: Query): HTMLTableRowElement {
  const tr = document.createElement('tr');
  tr.className = 'detail-row';
  const td = document.createElement('td');
  td.colSpan = columns.length;
  const node = detailTemplate.content.cloneNode(true) as DocumentFragment;
  node.querySelector('.query-text')!.textContent = q.text;

  const list = node.querySelector('.responses') as HTMLOListElement;
  if (q.responses.length === 0) list.innerHTML = '<li><em>No responses yet.</em></li>';
  else {
    list.innerHTML = q.responses
      .map(
        (r) =>
          `<li><span class="by">${esc(r.by)}</span> <small>(${esc(ROLE_STYLES[r.role].short)})</small><span class="at">${fmtDate(r.at)}</span><br/>${esc(r.text)}</li>`,
      )
      .join('');
  }

  const where = node.querySelector('.where') as HTMLParagraphElement;
  const dashboard = DASHBOARD_TITLES[q.dashboard] ?? q.dashboard;
  where.textContent = q.position
    ? `${dashboard} in the ${q.study} room (raised in VR)`
    : q.source === 'seed'
      ? `${dashboard} · pre-existing query from EDC`
      : `${dashboard} · raised from the browser`;
  const map = roomMapSvg(q);
  const svg = node.querySelector('.room-map') as SVGSVGElement;
  svg.setAttribute('viewBox', map.viewBox);
  svg.innerHTML = map.body;

  const meta = node.querySelector('.meta') as HTMLDListElement;
  const entries: [string, string][] = [
    ['ID', q.id],
    ['Dashboard', dashboard],
    ['Record', q.recordId],
    ['Source', q.source],
    ['Raised', fmtDate(q.raisedAt)],
  ];
  if (q.closedAt) entries.push(['Closed', `${fmtDate(q.closedAt)} by ${q.closedBy?.name ?? ''}`]);
  meta.innerHTML = entries.map(([k, v]) => `<dt>${esc(k)}</dt><dd>${esc(v)}</dd>`).join('');

  const form = node.querySelector('form.respond') as HTMLFormElement;
  const me = identity();
  const allowed = canModifyQueries(me.role);
  const textarea = form.querySelector('textarea')!;
  const submit = form.querySelector('button')!;
  const statusSelect = form.querySelector('select')!;
  const error = form.querySelector('.form-error') as HTMLSpanElement;
  if (!allowed) {
    textarea.disabled = submit.disabled = statusSelect.disabled = true;
    const tip = 'Sponsor Observers can view queries but cannot respond or change status';
    textarea.placeholder = tip;
    submit.title = statusSelect.title = tip;
  }
  if (q.status === 'Closed' || q.status === 'Cancelled') {
    textarea.disabled = submit.disabled = true;
    textarea.placeholder = `This query is ${q.status.toLowerCase()}. Reopen it to respond.`;
  }
  statusSelect.value = '';
  for (const opt of statusSelect.options) if (opt.value === q.status) opt.disabled = true;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    error.textContent = '';
    try {
      const updated = await sendJson<Query>('POST', api('queries', q.id, 'responses'), {
        by: me.name,
        role: me.role,
        text: textarea.value.trim(),
      });
      replaceItem(updated);
    } catch (err) {
      error.textContent =
        err instanceof ApiError
          ? Object.values(err.fields ?? {}).join('; ') || err.message
          : String(err);
    }
  });
  statusSelect.addEventListener('change', async () => {
    if (!statusSelect.value) return;
    error.textContent = '';
    try {
      const updated = await sendJson<Query>('PATCH', api('queries', q.id), {
        status: statusSelect.value,
        by: me.name,
        role: me.role,
      });
      replaceItem(updated);
    } catch (err) {
      error.textContent = err instanceof ApiError ? err.message : String(err);
      statusSelect.value = '';
    }
  });

  td.append(node);
  tr.append(td);
  return tr;
}

/**
 * Top-down study room from the room's plan (the curved hall, the studio bay, the ward room
 * sized for the query's study): the dashboards either side of the centre screen, the table,
 * the whiteboard, shelf and presentation display, the ward's rows, and the query's pin where
 * it was raised.
 */
function roomMapSvg(q: Query): { viewBox: string; body: string } {
  const subjects = manifest?.studies.find((s) => s.code === q.study)?.subjectCount ?? 50;
  const hall = hallOutline();
  const ward = wardRoom(subjects);
  const all = [...hall, ...ward.walls];
  const minX = Math.min(...all.map((p) => p.x)) - 0.6;
  const maxX = Math.max(...all.map((p) => p.x)) + 0.6;
  const minZ = Math.min(...all.map((p) => p.z)) - 0.6;
  const maxZ = Math.max(...all.map((p) => p.z)) + 0.6;
  // 240 units across, whatever the ward room's size.
  const k = 240 / (maxX - minX);
  const w = 240;
  const h = Math.round((maxZ - minZ) * k);
  const sx = (x: number) => ((x - minX) * k).toFixed(1);
  const sz = (z: number) => ((z - minZ) * k).toFixed(1);
  const path = (pts: Point[], close: boolean) =>
    `M${pts.map((p) => `${sx(p.x)} ${sz(p.z)}`).join(' L')}${close ? ' Z' : ''}`;
  const plan = arcPlan();
  const arc = plan.angles
    .map((a) => {
      const p = aroundArc(a, ARC.radius);
      return `<circle cx="${sx(p.x)}" cy="${sz(p.z)}" r="3" fill="#1f8a8a" opacity="0.8"/>`;
    })
    .join('');
  const c = aroundArc(CENTRE.angle, ARC.radius - CENTRE.inset);
  const cw = CENTRE.width * k;
  // The ward's seats for a study of this size, as dots.
  const seats = layoutWard(
    Array.from({ length: subjects }, (_, i) => ({ id: String(i).padStart(4, '0'), siteId: '' })),
  )
    .map((s) => `<circle cx="${sx(s.x)}" cy="${sz(s.z)}" r="1.1" fill="#b98b5a" opacity="0.7"/>`)
    .join('');
  const pin = q.position
    ? `<g transform="translate(${sx(q.position.x)},${sz(q.position.z)})"><circle r="7" fill="#b0233a" opacity="0.25"><animate attributeName="r" values="6;11;6" dur="2s" repeatCount="indefinite"/></circle><circle r="4" fill="#b0233a" stroke="#fff" stroke-width="1.5"/></g>`
    : `<text x="${w / 2}" y="${h - 8}" font-size="10" text-anchor="middle" fill="currentColor" opacity="0.6">no spatial position</text>`;
  const label = (x: number, z: number, t: string) =>
    `<text x="${sx(x)}" y="${sz(z)}" font-size="7" text-anchor="middle" fill="currentColor" opacity="0.7">${t}</text>`;
  const board = STUDIO_FITTINGS.boardZ;
  const body = `
    <path d="${path(hall, false)}" fill="none" stroke="currentColor" stroke-width="1.2" opacity="0.4"/>
    <path d="${path(ward.walls, false)}" fill="none" stroke="currentColor" stroke-width="1.2" opacity="0.4"/>
    <rect x="${sx(STUDIO.x0 + 0.1)}" y="${sz(board - 3)}" width="3" height="${(6 * k).toFixed(1)}" fill="#7a7f87" opacity="0.6"/>
    ${label((STUDIO.x0 - HALL.halfWidth) / 2, STUDIO.z1 - 0.6, 'studio')}
    <rect x="${sx(PRESENTATION.x - PRESENTATION.width / 2)}" y="${Number(sz(HALL.front)) - 3}" width="${(PRESENTATION.width * k).toFixed(1)}" height="3" fill="#1f3a6e" opacity="0.6"/>
    ${label(PRESENTATION.x, HALL.front - 1.2, 'presentation')}
    ${seats}
    ${label(WARD.focal.x + ward.backRadius / 2, WARD.focal.z + 0.4, 'ward')}
    <rect x="${(Number(sx(c.x)) - cw / 2).toFixed(1)}" y="${Number(sz(c.z)) - 2}" width="${cw.toFixed(1)}" height="4" fill="#1f8a8a"/>
    ${label(c.x, c.z + 1.6, 'centre screen')}
    ${arc}
    <circle cx="${sx(TABLE.x)}" cy="${sz(TABLE.z)}" r="${(1.5 * k).toFixed(1)}" fill="#b98b5a" opacity="0.6"/>
    <rect x="${sx(-1.7)}" y="${Number(sz(HALL.front)) - 3}" width="${(3.4 * k).toFixed(1)}" height="3" fill="#1f8a8a"/>
    ${pin}`;
  return { viewBox: `0 0 ${w} ${h}`, body };
}

function replaceItem(updated: Query): void {
  const i = items.findIndex((x) => x.id === updated.id);
  if (i >= 0) items[i] = updated;
  else items.unshift(updated);
  renderRows();
  void loadSummary();
}

/* ---------- Live updates ---------- */

const socket = new SocketClient();
const liveDot = $<HTMLSpanElement>('live-dot');
socket.on('welcome', () => liveDot.classList.add('on'));
socket.on('query:created', () => void load());
socket.on('query:updated', ({ query }) => {
  if (items.some((x) => x.id === query.id)) replaceItem(query);
  else void load();
});
socket.on('data:regenerated', () => void load());
socket.on('rooms:update', (m) => {
  rooms = m.rooms;
});
window.addEventListener('offline', () => liveDot.classList.remove('on'));
socket.connect();
// Queries page listens from the lobby so query broadcasts for every study reach it in later phases.
socket.send({
  type: 'hello',
  name: identity().name.padEnd(2, '_'),
  role: identity().role,
  avatar: 'web',
});
void rooms;

/* ---------- Boot ---------- */

$<HTMLAnchorElement>('home-link').href = page('app');
loadMeta()
  .then(load)
  .then(() => {
    if (location.hash === '#snapshots') showTab('snapshots');
  })
  .catch((err) => {
    $<HTMLParagraphElement>('status-line').textContent =
      `Could not load queries: ${(err as Error).message}`;
  });
